import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Sparkles, 
  RefreshCw,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Target,
  Building2,
  Lightbulb,
  ArrowRight,
  Clock,
  Calendar,
  Mail,
  Play,
  Settings2
} from 'lucide-react';
import api from '../lib/api';

const ReportsPage = () => {
  const { isExecutive, isOwner } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState({ pdf: false, excel: false });
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [enablingScheduler, setEnablingScheduler] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const fetchSchedulerStatus = async () => {
    if (!isOwner) return;
    try {
      const response = await api.get('/scheduler/status');
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  useEffect(() => {
    fetchSchedulerStatus();
  }, [isOwner]);

  const enableScheduler = async () => {
    try {
      setEnablingScheduler(true);
      const response = await api.post('/scheduler/enable-weekly-report');
      if (response.data.success) {
        toast.success('Reporte semanal programado para Lunes 6:00am EST');
        fetchSchedulerStatus();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Error al programar reporte');
    } finally {
      setEnablingScheduler(false);
    }
  };

  const sendTestReport = async () => {
    try {
      setSendingTest(true);
      const response = await api.post('/scheduler/send-test-report');
      if (response.data.success) {
        toast.success('Reporte de prueba enviado a tu email');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Error al enviar reporte de prueba');
    } finally {
      setSendingTest(false);
    }
  };

  const generateReport = async () => {
    try {
      setLoading(true);
      const response = await api.post('/reports/executive');
      setReport(response.data);
      toast.success('Reporte ejecutivo generado');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Error al generar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    try {
      setExporting(prev => ({ ...prev, pdf: true }));
      const response = await api.get('/export/pdf', { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `UrpeOS_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Error al exportar PDF');
    } finally {
      setExporting(prev => ({ ...prev, pdf: false }));
    }
  };

  const exportExcel = async () => {
    try {
      setExporting(prev => ({ ...prev, excel: true }));
      const response = await api.get('/export/excel', { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `UrpeOS_Data_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel descargado');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Error al exportar Excel');
    } finally {
      setExporting(prev => ({ ...prev, excel: false }));
    }
  };

  const getHealthColor = (health) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      warning: 'bg-amber-100 text-amber-700 border-amber-200',
      healthy: 'bg-emerald-100 text-emerald-700 border-emerald-200'
    };
    return colors[health] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getHealthLabel = (health) => {
    const labels = { critical: 'Crítico', warning: 'Advertencia', healthy: 'Saludable' };
    return labels[health] || health;
  };

  if (!isExecutive) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <FileText className="w-10 h-10 text-gray-300 mx-auto mb-4" />
        <h2 className="text-base font-medium text-gray-900 mb-2">Acceso Restringido</h2>
        <p className="text-sm text-gray-500">Solo Owner y Executive pueden generar reportes.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Reportes Ejecutivos
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Genera reportes semanales con análisis IA y exporta datos
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="default"
            size="sm"
            className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
            onClick={generateReport}
            disabled={loading}
            data-testid="generate-report-btn"
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            {loading ? 'Generando...' : 'Generar Reporte IA'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-gray-200"
            onClick={exportPDF}
            disabled={exporting.pdf}
            data-testid="export-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-1.5" />
            {exporting.pdf ? 'Exportando...' : 'Exportar PDF'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-gray-200"
            onClick={exportExcel}
            disabled={exporting.excel}
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />
            {exporting.excel ? 'Exportando...' : 'Exportar Excel'}
          </Button>
        </div>
      </div>

      {/* Scheduler Section - Owner Only */}
      {isOwner && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Reporte Semanal Automático</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  Envío automático cada <strong>Lunes 6:00am EST</strong> a todos los Owners y Executives
                </p>
                {schedulerStatus && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      schedulerStatus.email_configured 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {schedulerStatus.email_configured ? '✓ Email configurado' : '⚠ Email no configurado'}
                    </span>
                    {schedulerStatus.jobs?.length > 0 && (
                      <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                        ✓ Programado
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                onClick={sendTestReport}
                disabled={sendingTest || !schedulerStatus?.email_configured}
                data-testid="send-test-btn"
              >
                <Mail className="w-4 h-4 mr-1.5" />
                {sendingTest ? 'Enviando...' : 'Enviar Prueba'}
              </Button>
              <Button
                variant="default"
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={enableScheduler}
                disabled={enablingScheduler || !schedulerStatus?.email_configured}
                data-testid="enable-scheduler-btn"
              >
                <Play className="w-4 h-4 mr-1.5" />
                {enablingScheduler ? 'Activando...' : 'Activar Scheduler'}
              </Button>
            </div>
          </div>
          {schedulerStatus?.jobs?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-indigo-200">
              <p className="text-xs text-gray-600">
                <strong>Próximo envío:</strong> {schedulerStatus.jobs[0]?.next_run ? 
                  new Date(schedulerStatus.jobs[0].next_run).toLocaleString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                  }) : 'No programado'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* No Report Yet */}
      {!report && !loading && (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-base font-medium text-gray-900 mb-2">
            Genera tu Reporte Ejecutivo Semanal
          </h2>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
            La IA analizará el estado de todos tus OKRs y generará un reporte con 
            insights, prioridades y recomendaciones accionables.
          </p>
          <Button
            onClick={generateReport}
            className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generar Reporte
          </Button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-600">Analizando datos y generando insights...</p>
          <p className="text-xs text-gray-400 mt-1">Esto puede tomar unos segundos</p>
        </div>
      )}

      {/* Report Content */}
      {report && !loading && (
        <div className="space-y-6">
          {/* Health & Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Executive Summary */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-900">Resumen Ejecutivo</h2>
                <span className={`text-xs px-3 py-1 rounded-full border ${getHealthColor(report.report?.health_assessment)}`}>
                  {getHealthLabel(report.report?.health_assessment)}
                </span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                {report.report?.executive_summary}
              </p>
            </div>

            {/* Quick Stats */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-4">Métricas Clave</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Total KRs</span>
                  <span className="text-sm font-semibold">{report.summary?.total_krs}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Progreso Promedio</span>
                  <span className="text-sm font-semibold">{report.summary?.average_progress?.toFixed(1)}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">On Track</span>
                  <span className="text-sm font-semibold text-emerald-600">{report.summary?.status_breakdown?.on_track}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">En Riesgo</span>
                  <span className="text-sm font-semibold text-amber-600">{report.summary?.status_breakdown?.at_risk}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Priorities & Risks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Top Priorities */}
            {report.report?.top_priorities?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Target className="w-4 h-4 text-blue-500" />
                  Prioridades de la Semana
                </h2>
                <ol className="space-y-3">
                  {report.report.top_priorities.map((priority, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-600">{priority}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Risk Highlights */}
            {report.report?.risk_highlights?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Alertas de Riesgo
                </h2>
                <ul className="space-y-3">
                  {report.report.risk_highlights.map((risk, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-600 bg-amber-50 rounded-lg p-3">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      {risk}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Company Insights */}
          {report.report?.company_insights && Object.keys(report.report.company_insights).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-500" />
                Insights por Empresa
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {Object.entries(report.report.company_insights).map(([code, insight]) => (
                  <div key={code} className="bg-gray-50 rounded-lg p-4">
                    <span className="text-xs font-semibold text-gray-900 block mb-2">{code}</span>
                    <p className="text-xs text-gray-600">{insight}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Actions */}
          {report.report?.recommended_actions?.length > 0 && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-blue-500" />
                Acciones Recomendadas para Esta Semana
              </h2>
              <ul className="space-y-2">
                {report.report.recommended_actions.map((action, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <ArrowRight className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Forecast */}
          {report.report?.forecast && (
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                Proyección
              </h2>
              <p className="text-sm text-gray-600">{report.report.forecast}</p>
            </div>
          )}

          {/* Generated timestamp */}
          <p className="text-xs text-gray-400 text-center">
            Generado: {new Date(report.generated_at).toLocaleString('es-ES')}
          </p>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
